import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
} from "react";
import { useTranslation } from "react-i18next";
import {
    Modal,
    Table,
    Input,
    Select,
    Button,
    Typography,
    Tag,
    Empty,
    Space,
    Drawer,
    Divider,
    Rate,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import {
    CodeType,
    CompetitionRead,
    CompetitionSummary,
    PaginatedCompetitionsResponse,
    ProblemRead,
    ProblemType,
} from "@openapi";
import api from "@/lib/api";
import {
    EyeOutlined,
    SearchOutlined,
    StarFilled,
    TrophyOutlined,
} from "@ant-design/icons";
import ProblemStatementViewer from "./ProblemStatementViewer";
import ResizableTitle from "./ResizableTitle";
import EventPickerModal from "./events/EventPickerModal";
import EventProblemList from "./events/EventProblemList";
import { useAuth } from "@/context/AuthContext";
import { useEventMode } from "@/context/EventModeContext";
import {
    CODE_TYPE_CONFIG,
    codeTypeToProblemType,
    colorForProblemType,
    complexityToStars,
    problemTypeDisplayName,
} from "@/utils/api";

interface ChooseProblemModalProps {
    isVisible: boolean;
    onClose: () => void;
    onSelect: (problem: ProblemRead) => void;
    codeType: CodeType;
}

const ChooseProblemModal: React.FC<ChooseProblemModalProps> = ({
    isVisible,
    onClose,
    onSelect,
    codeType,
}) => {
    const { t } = useTranslation();
    const { isLoggedIn } = useAuth();
    const { eventId } = useEventMode();
    const [isEventPickerOpen, setIsEventPickerOpen] = useState(false);
    // Competitions load once per mount, not on every filter change.
    const competitionsRequestedRef = useRef(false);
    const [problems, setProblems] = useState<ProblemRead[]>([]);
    const [competitions, setCompetitions] = useState<CompetitionRead[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCompetition, setSelectedCompetition] = useState<
        string | null
    >(null);

    // Problem preview drawer state
    const [previewProblem, setPreviewProblem] = useState<ProblemRead>();
    const [isPreviewVisible, setIsPreviewVisible] = useState(false);
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ["5", "10", "20"],
        showTotal: (total) => t("chooseProblemModal.totalProblems", { total }),
    });

    const problemType = useMemo(
        () => codeTypeToProblemType(codeType),
        [codeType],
    );

    const config = useMemo(() => CODE_TYPE_CONFIG[codeType], [codeType]);

    // User-adjustable column widths (drag the right edge of a header)
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>({
        name: 360,
        problem_type: 110,
        competition: 220,
        complexity: 140,
        action: 190,
    });

    // Fetch problems with server-side pagination and filtering
    const fetchProblems = useCallback(
        async (
            page: number,
            pageSize: number,
            search?: string,
            competitionId?: string | null,
            problemType?: string | null,
        ) => {
            setLoading(true);
            try {
                const skip = (page - 1) * pageSize;
                const response = await api.problems.getProblems({
                    skip,
                    limit: pageSize,
                    search: search || undefined,
                    competitionId: competitionId || undefined,
                    problemType: problemType || undefined,
                });
                setProblems(response.items);
                setPagination((prev) => ({
                    ...prev,
                    current: page,
                    pageSize,
                    total: response.total,
                }));
            } catch (error) {
                console.error("Failed to fetch problems:", error);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    // Fetch competitions (once)
    const fetchCompetitions = useCallback(async () => {
        try {
            const data: PaginatedCompetitionsResponse | Array<CompetitionRead> =
                await api.competitions.getCompetitions();
            // Handle both paginated and non-paginated responses
            // If the response has an 'items' property, it's paginated
            if (typeof data === "object" && "items" in data) {
                setCompetitions(data.items);
            } else if (Array.isArray(data)) {
                setCompetitions(data);
            } else {
                console.warn(
                    "Unexpected competitions API response format:",
                    data,
                );
                setCompetitions([]);
            }
        } catch (error) {
            console.error("Failed to fetch competitions:", error);
            setCompetitions([]);
        }
    }, []);

    // Fetch when the modal opens and whenever a filter changes. Typing in the
    // search box is debounced; opening and other filters fetch right away.
    useEffect(() => {
        if (!isVisible || eventId) return;

        const timeoutId = setTimeout(
            () => {
                if (!competitionsRequestedRef.current) {
                    competitionsRequestedRef.current = true;
                    void fetchCompetitions();
                }
                void fetchProblems(
                    1,
                    pagination.pageSize || 10,
                    searchTerm,
                    selectedCompetition,
                    problemType,
                );
            },
            searchTerm ? 300 : 0,
        );

        return () => clearTimeout(timeoutId);
    }, [isVisible, eventId, searchTerm, selectedCompetition, problemType]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleTableChange = (newPagination: TablePaginationConfig) => {
        void fetchProblems(
            newPagination.current || 1,
            newPagination.pageSize || 10,
            searchTerm,
            selectedCompetition,
            problemType,
        );
    };

    // Handle preview
    const handlePreview = (problem: ProblemRead) => {
        setPreviewProblem(problem);
        setIsPreviewVisible(true);
    };

    const columns: ColumnsType<ProblemRead> = useMemo(
        () => [
            {
                title: t("chooseProblemModal.column.name"),
                dataIndex: "name",
                key: "name",
                sorter: (a: ProblemRead, b: ProblemRead) =>
                    a.name.localeCompare(b.name),
                render: (text: string, record: ProblemRead) => (
                    <div style={{ minWidth: 0 }}>
                        <Typography.Text
                            strong
                            ellipsis={{ tooltip: text }}
                            style={{ display: "block" }}
                        >
                            {text}
                        </Typography.Text>
                        {record.tags && record.tags.length > 0 && (
                            <Space size={4} wrap>
                                {record.tags.slice(0, 3).map((tag) => (
                                    <Tag key={tag} style={{ fontSize: 10 }}>
                                        {tag}
                                    </Tag>
                                ))}
                                {record.tags.length > 3 && (
                                    <Tag style={{ fontSize: 10 }}>
                                        +{record.tags.length - 3}
                                    </Tag>
                                )}
                            </Space>
                        )}
                    </div>
                ),
            },
            {
                title: t("chooseProblemModal.column.type"),
                dataIndex: "problem_type",
                key: "problem_type",
                render: (problemType: ProblemType) => (
                    <Tag color={colorForProblemType(problemType)}>
                        {problemTypeDisplayName(problemType)}
                    </Tag>
                ),
            },
            {
                title: t("chooseProblemModal.column.competition"),
                dataIndex: "competitions",
                key: "competition",
                render: (competitions: CompetitionSummary[] | undefined) => {
                    if (!competitions || competitions.length === 0)
                        return (
                            <Typography.Text type="secondary">
                                —
                            </Typography.Text>
                        );
                    const names = competitions.map((c) => c.name).join(", ");
                    return (
                        <Typography.Text
                            ellipsis={{ tooltip: names }}
                            style={{ display: "block" }}
                        >
                            {names}
                        </Typography.Text>
                    );
                },
            },
            {
                title: t("chooseProblemModal.column.difficulty"),
                dataIndex: "complexity",
                key: "complexity",
                sorter: (a, b) => (a.complexity || 0) - (b.complexity || 0),
                // Null for a problem seen only through a competition, which
                // has no practice difficulty.
                render: (complexity: number | null | undefined) =>
                    complexity != null && (
                        <Rate
                            disabled
                            value={complexityToStars(complexity)}
                            character={<StarFilled style={{ fontSize: 12 }} />}
                        />
                    ),
            },
            {
                title: t("chooseProblemModal.column.action"),
                key: "action",
                render: (_: unknown, record: ProblemRead) => (
                    <Space>
                        <Button
                            type="link"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => handlePreview(record)}
                        >
                            {t("chooseProblemModal.preview")}
                        </Button>
                        <Button
                            type="primary"
                            size="small"
                            onClick={() => onSelect(record)}
                        >
                            {t("chooseProblemModal.select")}
                        </Button>
                    </Space>
                ),
            },
        ],
        [onSelect, t],
    );

    const resizableColumns: ColumnsType<ProblemRead> = useMemo(
        () =>
            columns.map((col) => {
                const key = String(col.key);
                const width = columnWidths[key];
                return {
                    ...col,
                    width,
                    onHeaderCell: () =>
                        ({
                            width,
                            onResize: (newWidth: number) =>
                                setColumnWidths((prev) => ({
                                    ...prev,
                                    [key]: newWidth,
                                })),
                        }) as React.HTMLAttributes<HTMLElement>,
                };
            }),
        [columns, columnWidths],
    );

    const totalWidth = useMemo(
        () => Object.values(columnWidths).reduce((sum, w) => sum + w, 0),
        [columnWidths],
    );

    return (
        <>
            <Modal
                title={t("chooseProblemModal.title")}
                open={isVisible}
                onCancel={onClose}
                footer={null}
                width={1100}
                styles={{ body: { padding: "16px 24px" } }}
            >
                {eventId ? (
                    <EventProblemList
                        problemType={problemType}
                        onSelect={onSelect}
                        onChangeEvent={() => setIsEventPickerOpen(true)}
                    />
                ) : (
                    <>
                        {/* Filters */}
                        <div
                            style={{
                                display: "flex",
                                gap: 12,
                                marginBottom: 16,
                                flexWrap: "wrap",
                            }}
                        >
                            <Input
                                placeholder={t(
                                    "chooseProblemModal.searchByName",
                                )}
                                prefix={<SearchOutlined />}
                                value={searchTerm}
                                onChange={handleSearch}
                                style={{ flex: 1, minWidth: 200 }}
                                allowClear
                            />
                            <Select
                                placeholder={t(
                                    "chooseProblemModal.filterByCompetition",
                                )}
                                onChange={setSelectedCompetition}
                                value={selectedCompetition}
                                style={{ flex: 1, minWidth: 300 }}
                                allowClear
                                showSearch={{ optionFilterProp: "label" }}
                                options={competitions.map((competition) => ({
                                    value: competition.id,
                                    label: competition.name,
                                }))}
                            />
                            {isLoggedIn && (
                                <Button
                                    icon={<TrophyOutlined />}
                                    onClick={() => setIsEventPickerOpen(true)}
                                >
                                    {t("events.picker.open")}
                                </Button>
                            )}
                        </div>

                        {/* Info about server-side language filtering */}
                        <div style={{ marginBottom: 12 }}>
                            <Typography.Text type="secondary">
                                {t("chooseProblemModal.showingPrefix")}{" "}
                                <Tag color={config.color}>
                                    {problemTypeDisplayName(problemType)}
                                </Tag>{" "}
                                {t("chooseProblemModal.showingSuffix")}
                            </Typography.Text>
                        </div>
                        <Table
                            dataSource={problems}
                            columns={resizableColumns}
                            components={{ header: { cell: ResizableTitle } }}
                            tableLayout="fixed"
                            scroll={{ x: totalWidth }}
                            rowKey="id"
                            loading={loading}
                            pagination={pagination}
                            onChange={handleTableChange}
                            size="small"
                            locale={{
                                emptyText: (
                                    <Empty
                                        description={
                                            loading
                                                ? t(
                                                      "chooseProblemModal.loadingProblems",
                                                  )
                                                : t(
                                                      "chooseProblemModal.noProblemsFound",
                                                  )
                                        }
                                    />
                                ),
                            }}
                        />
                    </>
                )}
            </Modal>

            <EventPickerModal
                open={isEventPickerOpen}
                onClose={() => setIsEventPickerOpen(false)}
            />

            {/* Problem Preview Drawer */}
            <Drawer
                title={
                    <Space>
                        <span>{previewProblem?.name}</span>
                        {previewProblem?.complexity != null && (
                            <Rate
                                disabled
                                value={complexityToStars(
                                    previewProblem.complexity,
                                )}
                                character={
                                    <StarFilled style={{ fontSize: 12 }} />
                                }
                            />
                        )}
                    </Space>
                }
                placement="right"
                size="large"
                open={isPreviewVisible}
                onClose={() => setIsPreviewVisible(false)}
                extra={
                    previewProblem && (
                        <Button
                            type="primary"
                            onClick={() => {
                                onSelect(previewProblem);
                                setIsPreviewVisible(false);
                            }}
                        >
                            {t("chooseProblemModal.selectThisProblem")}
                        </Button>
                    )
                }
            >
                {previewProblem && (
                    <div style={{ height: "100%" }}>
                        {/* Problem metadata */}
                        <div style={{ marginBottom: 16 }}>
                            <Space wrap>
                                <Tag
                                    color={colorForProblemType(
                                        previewProblem.problem_type,
                                    )}
                                >
                                    {problemTypeDisplayName(
                                        previewProblem.problem_type,
                                    )}
                                </Tag>
                                {previewProblem.competitions?.map(
                                    (competition) => (
                                        <Tag
                                            key={competition.id}
                                            color="purple"
                                        >
                                            {competition.name}
                                        </Tag>
                                    ),
                                )}
                                <Tag>
                                    {t("chooseProblemModal.points", {
                                        count: previewProblem.points,
                                    })}
                                </Tag>
                                {previewProblem.tags?.map((tag) => (
                                    <Tag key={tag}>{tag}</Tag>
                                ))}
                            </Space>
                        </div>
                        <Divider style={{ margin: "12px 0" }} />
                        {/* Problem statement with KaTeX */}
                        <ProblemStatementViewer
                            statement={previewProblem.statement}
                            problemName={previewProblem.name}
                            points={previewProblem.points}
                        />
                    </div>
                )}
            </Drawer>
        </>
    );
};

export default ChooseProblemModal;
